import { useRef, useState } from "react";
import { Loader2, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  getApiErrorDetail,
  uploadCaseDocument,
} from "@/lib/api";

const MARINE_DEEP = "oklch(0.115 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";
const TEXT_DIM = "oklch(0.85 0.01 243)";

interface DocumentUploadProps {
  caseId: number;
  onUploaded: () => void;
}

// Composant minimal de depot multipart vers POST /api/v1/documents/.
// Cote backend : hash streaming, detection Magic Bytes, TSA Zetes,
// ancrage Merkle -- transaction atomique. Le frontend ne fait que
// remettre le fichier ; toute la chaine probatoire vit cote core.
export function DocumentUpload({ caseId, onUploaded }: DocumentUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onSubmit = async () => {
    if (!file || submitting) return;
    setSubmitting(true);
    try {
      const res = await uploadCaseDocument(caseId, file);
      toast.success(
        `Piece deposee. Hash : ${res.file_integrity_hash.slice(0, 12)}…`,
      );
      reset();
      onUploaded();
    } catch (err) {
      toast.error(
        getApiErrorDetail(err) ?? "Echec du depot. Reessayez.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="rounded-sm border p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:items-center"
      style={{
        borderColor: `color-mix(in oklch, ${GOLD} 25%, transparent)`,
        background: "color-mix(in oklch, oklch(1 0 0) 2%, transparent)",
      }}
    >
      <div className="flex-1">
        <div
          className="text-xs uppercase tracking-widest mb-1"
          style={{ color: GOLD, letterSpacing: "0.15em" }}
        >
          <Paperclip size={11} className="inline mr-1" />
          Depot d'une piece probatoire
        </div>
        <input
          ref={inputRef}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          disabled={submitting}
          className="text-xs mt-1 block max-w-full"
          style={{ color: TEXT_DIM }}
        />
        {file && (
          <div className="mt-1 text-xs" style={{ color: TEXT_DIM }}>
            {file.name} — {(file.size / 1024).toFixed(1)} ko
          </div>
        )}
      </div>
      <Button
        onClick={onSubmit}
        disabled={!file || submitting}
        style={{
          background: GOLD,
          color: MARINE_DEEP,
          opacity: !file || submitting ? 0.6 : 1,
        }}
      >
        {submitting ? (
          <>
            <Loader2 size={14} className="animate-spin mr-2" />
            Materialisation…
          </>
        ) : (
          <>
            <Upload size={14} className="mr-2" />
            Deposer
          </>
        )}
      </Button>
    </div>
  );
}
